import type { AudioTrackList, TextTrackList } from '../index.h'

import videojs from 'video.js'

// if 'window.chrome.cast' is missing it may said that 'appReceiverId' not regestried
// if 'window.cast' is missing - cast api not yet loaded
export function isChromecastAPIAvailable(): boolean {
  return Boolean(window.chrome && window.cast && window.chrome.cast)
}

export const noop = () => undefined

/**
 * Equality for numeric arrays, ignoring order (but respecting multiplicity).
 * Used to de-duplicate Chromecast track ID requests.
 *
 * - [1, 2] vs [2, 1] -> true
 * - [1, 1] vs [1, 1] -> true
 * - [1, 2] vs [1, 1] -> false
 * - [1, 2] vs [2, 2] -> false
 * - [1] vs [1, 2] -> false
 * - [1, 2] vs [1] -> false
 */
export const isSameNumbersArrays = (a?: number[], b?: number[]): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false

  const one = [...a].sort((x, y) => x - y)
  const two = [...b].sort((x, y) => x - y)

  for (let i = 0; i < a.length; i++) {
    if (one[i] !== two[i]) return false
  }
  return true
}

/**
 * Returns true when language tags are equal or one is a prefix of the other.
 * Helps match `en` vs `en-US` style language codes. Case insensitive.
 */
export const isSameOrPrefixLanguage = (a?: string, b?: string): boolean => {
  if (!a || !b) return false
  const left = a.toLowerCase()
  const right = b.toLowerCase()
  return left.startsWith(right) || right.startsWith(left)
}

/**
 * Mirrors receiver tracks into sender-side Video.js track lists.
 *
 * Notes:
 * - Some sources have duplicate tracks with the same language; we only enable/show the first match.
 * - Video.js `TextTrack` runtime expects a `tech` reference; typings don't include it, so we cast.
 */
export const addReceiverTracksToSenderTrackLists = (args: {
  tracks: chrome.cast.media.Track[]
  audioTracks?: AudioTrackList
  textTracks?: TextTrackList
  initialAudioLanguage?: string
  initialTextLanguage?: string
  tech: any
}): void => {
  const {
    tracks,
    audioTracks,
    textTracks,
    initialAudioLanguage,
    initialTextLanguage,
    tech,
  } = args

  let hasEnabledAudio = false
  let hasShownText = false

  for (const track of tracks) {
    const { type, name, language, trackId } = track

    if (
      type === chrome.cast.media.TrackType.AUDIO &&
      initialAudioLanguage &&
      audioTracks
    ) {
      const matchesLang = isSameOrPrefixLanguage(initialAudioLanguage, language)
      audioTracks.addTrack(
        new videojs.AudioTrack({
          language,
          label: name,
          id: trackId,
          enabled: !hasEnabledAudio && matchesLang,
          loaded_: true,
        })
      )
      if (!hasEnabledAudio && matchesLang) hasEnabledAudio = true
    } else if (
      type === chrome.cast.media.TrackType.TEXT &&
      initialTextLanguage &&
      textTracks
    ) {
      const matchesLang = isSameOrPrefixLanguage(initialTextLanguage, language)
      textTracks.addTrack(
        new (videojs.TextTrack as any)({
          tech,
          language,
          label: name,
          id: trackId,
          loaded_: true,
          kind: 'subtitles',
          mode: !hasShownText && matchesLang ? 'showing' : 'hidden',
        })
      )
      if (!hasShownText && matchesLang) hasShownText = true
    }
  }
}

// this indicates if it is makes sense to load cast scripts
export function isChromiumBased() {
  const navigatorObj = navigator as any
  return (
    navigatorObj.userAgentData &&
    navigatorObj.userAgentData.brands?.some((b) =>
      b.brand.toLowerCase().includes('chromium')
    )
  )
}

export const SESSION_TIMEOUT = 10 * 1000 // 10 sec

export const clearTracks = (
  list: AudioTrackList | TextTrackList,
  tech?: { removeRemoteTextTrack: any }
) => {
  if (!list) return

  let i = list.length
  while (i--) {
    const track = list[i]

    if (list instanceof videojs.TextTrackList) {
      tech?.removeRemoteTextTrack?.(track)
    }
    list.removeTrack(track)
  }
}

export const getSelectedAudioTrack = (audioTracks?: AudioTrackList) => {
  let initialAudio
  if (audioTracks)
    for (let i = 0; i < audioTracks.length; i++) {
      const { enabled, id, kind, label, language } = audioTracks[i]
      if (enabled) {
        initialAudio = { enabled, id, kind, label, language }
        break
      }
    }

  return initialAudio
}

export const getSelectedTextTrack = (textTracks?: TextTrackList) => {
  let initialText
  if (textTracks)
    for (let i = 0; i < textTracks.length; i++) {
      const {
        default: isDefault,
        id,
        kind,
        label,
        language,
        mode,
      } = textTracks[i]
      if ((kind === 'captions' || kind === 'subtitles') && mode === 'showing') {
        initialText = { default: isDefault, id, kind, label, language, mode }
        break
      }
    }

  return initialText
}
